import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, MessageSquare, TrendingUp, Calendar, BarChart3, Trash2, Building2 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const COLORS = ['#EF4444', '#F59E0B', '#F59E0B', '#10B981', '#10B981'];
const RATING_LABELS = {
  1: "Sehr schlecht",
  2: "Schlecht",
  3: "Mittelmäßig",
  4: "Gut",
  5: "Sehr gut"
};

const Feedback = () => {
  const [stats, setStats] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    subdomain: "all",
    rating: "all",
    timeRange: "all",
    hasComment: "all"
  });
  const [subdomains, setSubdomains] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Helper to ensure we always have an array
  const toArray = (data) => Array.isArray(data) ? data : [];

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, feedbacksRes] = await Promise.all([
        axios.get(`${API}/feedback/stats`),
        axios.get(`${API}/feedback/all`, {
          params: {
            subdomain: filters.subdomain !== "all" ? filters.subdomain : undefined,
            min_rating: filters.rating !== "all" ? parseInt(filters.rating) : undefined,
            max_rating: filters.rating !== "all" ? parseInt(filters.rating) : undefined,
            has_comment: filters.hasComment !== "all" ? filters.hasComment === "true" : undefined,
            limit: 50
          }
        })
      ]);
      
      setStats(statsRes.data);
      setFeedbacks(feedbacksRes.data.feedbacks);
      
      // Extract unique subdomains
      const uniqueSubdomains = toArray(statsRes.data.per_subdomain).map(s => ({
        value: s._id,
        label: s.display_name
      }));
      setSubdomains(uniqueSubdomains);
    } catch (error) {
      toast.error("Fehler beim Laden der Feedbacks");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 sm:w-5 sm:h-5 ${
          index < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
        }`}
      />
    ));
  };

  const getRatingDistributionData = () => {
    if (!stats || !stats.rating_distribution) return [];
    return Object.entries(stats.rating_distribution).map(([rating, count]) => ({
      rating: `${rating} ⭐`,
      count: count,
      label: RATING_LABELS[rating]
    }));
  };

  const handleDeleteClick = (feedback) => {
    setFeedbackToDelete(feedback);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!feedbackToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/feedback/${feedbackToDelete.id}`);
      toast.success("Feedback erfolgreich gelöscht");
      setShowDeleteDialog(false);
      setFeedbackToDelete(null);
      loadData(); // Reload data
    } catch (error) {
      toast.error("Fehler beim Löschen des Feedbacks");
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Lädt Feedbacks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="feedback-container">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Feedback Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-600">Feedback von allen Kantine-Apps</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="card-white card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Gesamt</CardTitle>
            <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.total}</div>
            <p className="text-xs text-gray-600 mt-1">Feedbacks</p>
          </CardContent>
        </Card>

        <Card className="card-white card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Ø Bewertung</CardTitle>
            <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-yellow-600 flex items-center gap-1">
              {(stats.avg_rating || 0).toFixed(1)}
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            </div>
            <p className="text-xs text-gray-600 mt-1">von 5 Sternen</p>
          </CardContent>
        </Card>

        <Card className="card-white card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Mit Kommentar</CardTitle>
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.with_comment}</div>
            <p className="text-xs text-gray-600 mt-1">{stats.total > 0 ? Math.round((stats.with_comment / stats.total) * 100) : 0}% der Feedbacks</p>
          </CardContent>
        </Card>

        <Card className="card-white card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Diese Woche</CardTitle>
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.this_week}</div>
            <p className="text-xs text-gray-600 mt-1">Letzte 7 Tage</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Rating Distribution */}
        <Card className="card-white">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Bewertungsverteilung
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={getRatingDistributionData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="rating" 
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                    formatter={(value, name, props) => [value, props.payload.label]}
                  />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {getRatingDistributionData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Per Subdomain */}
        <Card className="card-white">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg text-gray-900">Feedbacks pro Kantine</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {toArray(stats.per_subdomain).slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{item.display_name}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      <span className="text-xs text-gray-600">{(item.avg_rating || 0).toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">{item.count}</p>
                    <p className="text-xs text-gray-600">Feedbacks</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-white">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-gray-900">Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Kantine</label>
              <Select value={filters.subdomain} onValueChange={(value) => setFilters({...filters, subdomain: value})}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-300">
                  <SelectItem value="all">Alle Kantinen</SelectItem>
                  {toArray(subdomains).map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Bewertung</label>
              <Select value={filters.rating} onValueChange={(value) => setFilters({...filters, rating: value})}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-300">
                  <SelectItem value="all">Alle Bewertungen</SelectItem>
                  <SelectItem value="5">5 Sterne ⭐⭐⭐⭐⭐</SelectItem>
                  <SelectItem value="4">4 Sterne ⭐⭐⭐⭐</SelectItem>
                  <SelectItem value="3">3 Sterne ⭐⭐⭐</SelectItem>
                  <SelectItem value="2">2 Sterne ⭐⭐</SelectItem>
                  <SelectItem value="1">1 Stern ⭐</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Kommentar</label>
              <Select value={filters.hasComment} onValueChange={(value) => setFilters({...filters, hasComment: value})}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-300">
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="true">Mit Kommentar</SelectItem>
                  <SelectItem value="false">Ohne Kommentar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback List */}
      <div className="space-y-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
          Alle Feedbacks ({toArray(feedbacks).length})
        </h2>
        
        {toArray(feedbacks).length === 0 ? (
          <Card className="card-white">
            <CardContent className="py-12 text-center text-gray-600">
              Keine Feedbacks gefunden
            </CardContent>
          </Card>
        ) : (
          toArray(feedbacks).map((feedback) => (
            <Card key={feedback.id} className="card-white card-hover">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex gap-0.5">
                        {renderStars(feedback.rating)}
                      </div>
                      <span className="text-sm font-semibold text-gray-700">
                        {feedback.rating}.0
                      </span>
                    </div>

                    {/* Comment */}
                    {feedback.comment && feedback.comment.trim() !== "" && (
                      <div className="mb-3">
                        <p className="text-gray-800 text-sm sm:text-base leading-relaxed">
                          &quot;{feedback.comment}&quot;
                        </p>
                      </div>
                    )}

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        <span>{feedback.subdomain_display || `Kantine ${feedback.subdomain}`}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(feedback.timestamp)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Badge */}
                  <div className="flex items-start">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      feedback.rating >= 4 
                        ? 'bg-green-100 text-green-700' 
                        : feedback.rating === 3 
                        ? 'bg-yellow-100 text-yellow-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {RATING_LABELS[feedback.rating]}
                    </span>
                    <button
                      onClick={() => handleDeleteClick(feedback)}
                      className="ml-2 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Feedback löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Feedback löschen?</DialogTitle>
          </DialogHeader>
          {feedbackToDelete && (
            <div className="py-4">
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Bewertung:</span>
                  <div className="flex gap-0.5">
                    {renderStars(feedbackToDelete.rating)}
                  </div>
                </div>
                {feedbackToDelete.comment && (
                  <div>
                    <span className="text-sm text-gray-600">Kommentar:</span>
                    <p className="text-sm text-gray-900 mt-1">&quot;{feedbackToDelete.comment}&quot;</p>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Kantine:</span>
                  <span className="text-gray-900">{feedbackToDelete.subdomain_display || feedbackToDelete.subdomain}</span>
                </div>
              </div>
              <p className="text-sm text-red-600 mt-4">
                Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? "Löscht..." : "Löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Feedback;
